import { API, graphqlOperation } from 'aws-amplify'
import { useEffect, useState } from 'react'

const op = `query ListTeams($d1: String) {
    listTeams(d1: $d1) {
        items {
            pk
            sk
            d1
            contact {
                sk
                d2
                firstName
                lastName
                email
            }
            createdAt
            createdBy
            d1
            description
            icon
            name
            reportAsHere
            trainingPrograms
            visibility
            active
            nrOfMembers
            groups
            modifiedAt
            modifiedBy
            positions {
                pk
                sk
                title
            }
        }
    }
}`

const cache = {}

// Mounted useListTeams instances, so cache mutations (e.g. a member declining
// their assignment while the All Teams page is open) update the page live
// instead of waiting for a remount refetch.
const subscribers = new Map()

const subscribe = (d1, callback) => {
    if (!subscribers.has(d1)) subscribers.set(d1, new Set())
    subscribers.get(d1).add(callback)
    return () => {
        const forD1 = subscribers.get(d1)
        if (!forD1) return
        forD1.delete(callback)
        if (forD1.size === 0) subscribers.delete(d1)
    }
}

const notify = (d1) => {
    const forD1 = subscribers.get(d1)
    if (!forD1 || !cache[d1]) return
    forD1.forEach((callback) => callback(cache[d1]))
}

const storeTeams = (d1, entry) => {
    cache[d1] = entry
}

// Optimistically adjust a team's member count in every cached team list (the
// cache is keyed by building d1) so pages that render team.nrOfMembers show
// the change immediately — e.g. after a member declines their assignment.
export const adjustTeamMemberCountInCache = (teamPk, delta) => {
    Object.keys(cache).forEach((d1) => {
        const entry = cache[d1]
        if (!entry?.items) return
        let touched = false
        const items = entry.items.map((t) => {
            if (t.pk !== teamPk) return t
            touched = true
            return { ...t, nrOfMembers: Math.max((t.nrOfMembers ?? 0) + delta, 0) }
        })
        if (touched) {
            cache[d1] = { ...entry, items }
            notify(d1)
        }
    })
}

function useListTeams(d1) {
    const [error, setErrors] = useState(null)
    const [loading, setLoading] = useState(true)
    const [teams, setTeams] = useState([])

    useEffect(() => {
        // Ignore responses that arrive after d1 has changed (or the component
        // unmounted) so a stale fetch can't clobber the current team list.
        let stale = false

        const handleTeams = (_teams) => {
            if (stale) return
            setTeams(_teams.items)
            setLoading(false)
        }

        async function fetch() {
            const cached = cache[d1]
            if (cached) handleTeams(cached)
            try {
                const response = await API.graphql(graphqlOperation(op, { d1, sk: 'TEAM' }))
                const fresh = response.data.listTeams
                if (stale) return
                storeTeams(d1, fresh)
                handleTeams(fresh)
            } catch (response) {
                console.error(response.errors)
                if (!stale) setErrors(response.errors)
            }
        }

        if (!d1) {
            // d1 can go null on a live change (e.g. the Teams tool toggled off
            // while this page is mounted) — drop any previously fetched list
            // so consumers don't keep rendering stale team data.
            setTeams([])
            setLoading(false)
            return undefined
        }

        const unsubscribe = subscribe(d1, handleTeams)
        setLoading(true)
        fetch()
        return () => {
            stale = true
            unsubscribe()
        }
    }, [d1])

    return { error, loading, teams }
}

export default useListTeams
